<template>
  <b-card class="mb-3" :class="background_color_class">
    <b-row>
      <b-col cols="11">
        <b-row>
          <b-col>
            <b-form-group>
              <b-form-select
                v-model="lhs_table_name"
                type="text"
                :disabled="!editing"
                :options="possible_tables_options"
              />
            </b-form-group>
          </b-col>

          <b-col>
            <b-form-group>
              <b-form-select
                v-model="rhs_table_name"
                type="text"
                :disabled="!editing"
                :options="possible_tables_options"
              />
            </b-form-group>
          </b-col>
        </b-row>
      </b-col>
      <b-col>
        <b-button
          v-show="local_delta_mode !== ''"
          class="float-right"
          variant="outline-secondary"
          @click="toggle_editing()"
        >
          <span v-if="editing" class="fas fa-check" />
          <span v-else class="fas fa-pen" />
        </b-button>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="11">
        <b-row>
          <b-col v-show="local_delta_mode !== 'substract'" cols="5">
            <div v-if="!editing">
              <b-list-group-item
                v-for="column in id_columns_lhs"
                :key="JSON.stringify(column)"
              >
                {{ column.name }}
              </b-list-group-item>
            </div>
            <columns-choice-options
              v-else
              v-model="id_columns_lhs"
              :available_column_names="listing_lhs"
            >
              <template #sub_component="props">
                <generic-column-name-edition
                  v-bind="props"
                  v-on="props.listeners"
                />
              </template>
            </columns-choice-options>
          </b-col>
          <b-col v-show="local_delta_mode !== 'substract'" cols="2">
            <b-form-group>
              <b-form-select
                v-model="relation_type"
                type="text"
                :disabled="!editing"
                :options="relation_type_options"
              />
            </b-form-group>
          </b-col>
          <b-col v-show="local_delta_mode !== 'substract'" cols="5">
            <b-list-group>
              <div v-if="!editing">
                <b-list-group-item
                  v-for="column in id_columns_rhs"
                  :key="JSON.stringify(column)"
                >
                  {{ column.name }}
                </b-list-group-item>
              </div>
              <columns-choice-options
                v-else
                v-model="id_columns_rhs"
                :available_column_names="listing_rhs"
              >
                <template #sub_component="props">
                  <generic-column-name-edition
                    v-bind="props"
                    v-on="props.listeners"
                  />
                </template>
              </columns-choice-options>
            </b-list-group>
          </b-col>
        </b-row>
        <b-row v-if="local_delta_mode !== ''">
          <b-col cols="4" />
          <b-col cols="4">
            <b-form-select
              v-model="local_delta_mode"
              class="mt-2"
              :options="delta_options"
              :disabled="!editing"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-col cols="1">
        <b-button
          v-show="local_delta_mode !== ''"
          class="float-right"
          variant="outline-secondary"
          @click="show_delete_modal"
        >
          <span class="fa fa-trash" />
        </b-button>
      </b-col>
    </b-row>

    <b-modal
      ref="delete_modal"
      :title="$t('relationship_deletion')"
      @ok="delete_relation"
      >{{ $t('confirm_deletion') }}
      <div slot="modal-ok">Supprimer</div>
      <div slot="modal-cancel">Annuler</div>
    </b-modal>
  </b-card>
</template>

<style lang="scss" scoped>
@import '@/custom-bootstrap.scss';
.light-green-bg {
  background-color: $lightgreen;
}

.light-red-bg {
  background-color: $lightred;
}
</style>

<i18n>
{
  "en": {
    "relationship_deletion": "Relationship deletion",
    "confirm_deletion": "Are you sure you want to permanently delete this relationship ?"
  },
  "fr": {
    "relationship_deletion": "Suppression de la relation",
    "confirm_deletion": "Êtes-vous sûr de vouloir définitivement supprimer cette relation ?"
  }
}
</i18n>

<script>
import _ from 'lodash'

import { mapGetters } from 'vuex'

import { table_columns } from '@/api/subscriptions.js'

import { subscription_mixin } from '@/components/mixin.js'

import ColumnsChoiceOptions from './columns_choice_options.vue'
import GenericColumnNameEdition from '@/components/generic_column_name_edition.vue'

// *****************************************************************************

export default {
  name: 'BrickAggregationSubcomponentRelationEdition',
  components: {
    ColumnsChoiceOptions,
    GenericColumnNameEdition,
  },
  mixins: [subscription_mixin],
  props: [
    'relation',
    'delta_mode',
    'project_id',
    'possible_tables_options',
    'relation_type_options',
    'uid_from_name_func',
  ],

  data() {
    return {
      editing: false,
      lhs_table_name: '',
      rhs_table_name: '',
      id_columns_lhs: [],
      id_columns_rhs: [],
      relation_type: -1,

      listing_lhs: [],
      listing_rhs: [],

      local_delta_mode: '',
      delta_options: [
        { value: 'add', text: this.$t('add') },
        { value: 'substract', text: this.$t('substract') },
      ],
    }
  },

  computed: {
    relation_name(relation_nb) {
      let rel = this.relation_type_options.find(function(element) {
        return Object.values(element)[1] == relation_nb
      })

      return rel.text
    },
    adapted_data() {
      let adapted = {
        lhs_table_name: this.lhs_table_name,
        rhs_table_name: this.rhs_table_name,
        id_columns_lhs: _.cloneDeep(this.id_columns_lhs),
        id_columns_rhs: _.cloneDeep(this.id_columns_rhs),

        relation_type: this.relation_type,
      }
      return adapted
    },
    background_color_class() {
      let res = []
      if (this.local_delta_mode === 'add') {
        res.push('light-green-bg')
      } else if (this.local_delta_mode === 'substract') {
        res.push('light-red-bg')
      } else {
        res.push('bg-light')
      }

      return res
    },
  },

  watch: {
    relation: {
      immediate: true,
      async handler(new_val) {
        new_val = new_val || {}
        this.lhs_table_name = new_val.lhs_table_name
        this.rhs_table_name = new_val.rhs_table_name
        this.relation_type = new_val.relation_type

        this.id_columns_lhs = _.cloneDeep(new_val.id_columns_lhs)

        this.id_columns_rhs = _.cloneDeep(new_val.id_columns_rhs)

        let lhs_table_uid = this.uid_from_name_func(this.lhs_table_name)
        this.listing_lhs =
          (await table_columns(this.project_id, lhs_table_uid)).pipe_object ||
          []

        let rhs_table_uid = this.uid_from_name_func(this.rhs_table_name)
        this.listing_rhs =
          (await table_columns(this.project_id, rhs_table_uid)).pipe_object ||
          []

        // this.listing_lhs =
        //   (await table_columns_from_name(this.project_id, this.lhs_table_name))
        //     .pipe_object || []
        // this.listing_rhs =
        //   (await table_columns_from_name(this.project_id, this.rhs_table_name))
        //     .pipe_object || []
      },
    },

    async lhs_table_name() {
      this.listing_lhs = []

      let lhs_table_uid = this.uid_from_name_func(this.lhs_table_name)
      this.listing_lhs =
        (await table_columns(this.project_id, lhs_table_uid)).pipe_object || []
      // this.listing_lhs =
      //   (await table_columns_from_name(this.project_id, this.lhs_table_name))
      //     .pipe_object || []
      this.id_columns_lhs = []
    },

    async rhs_table_name() {
      this.listing_rhs = []

      let rhs_table_uid = this.uid_from_name_func(this.rhs_table_name)
      this.listing_rhs =
        (await table_columns(this.project_id, rhs_table_uid)).pipe_object || []
      // this.listing_rhs =
      //   (await table_columns_from_name(this.project_id, this.rhs_table_name))
      //     .pipe_object || []
      this.id_columns_rhs = []
    },

    delta_mode: {
      immediate: true,
      handler(nv, ov) {
        if (!_.isEqual(nv, ov)) {
          this.local_delta_mode = nv
        }
      },
    },
  },
  beforeMount() {
    this.fetch_collections(['tables'])
  },
  methods: {
    toggle_editing() {
      if (
        !(
          this.local_delta_mode === 'add' &&
          (_.isEmpty(this.id_columns_lhs) ||
            _.isEmpty(this.id_columns_rhs) ||
            this.relation_type === -1)
        )
      ) {
        if (this.local_delta_mode !== 'add') {
          this.id_columns_lhs = []
          this.id_columns_rhs = []
          this.relation_type = -1
        }
        if (this.editing) {
          this.$emit('validate', this.adapted_data)
          this.$emit('changed_delta_mode', this.local_delta_mode)
        }
        this.editing = !this.editing
      }
    },
    show_delete_modal() {
      this.$refs.delete_modal.show()
    },

    delete_relation(evt) {
      // We need to bypass the standard way the modal is hidden
      // Indeed, the $emit event will delete this component
      // and thus the hide operation would not be fully completed
      evt.preventDefault()
      this.$refs.delete_modal.hide()
      this.$emit('deleted')
    },
  },
}
</script>
